import { DownloadIcon, PencilIcon, UploadIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Header } from '../../Components/Header/Header';
import { SearchableHeader } from '../../Components/Search/SearchableHeader';
import { CompanySelect } from '../../Components/Select/CompanySelect';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import {
  FormActions,
  FormCancelButton,
  FormSubmitButton,
} from '../../Components/Form';
import { decode } from '../../Services/DecodeToken';
import apiClient from '../../Services/ApiClient';
import apiClientFormData from '../../Services/ApiClientFormData';
import { useSnackbar } from '../../Context/SnackbarProvider';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { useColumnSearches } from '../../Hooks/useColumnSearch';
import {
  AddButton,
  BulkImportLink,
  CloseButton,
  EditButton,
  ModalHeader,
  ModalOverlay,
  PageContainer,
} from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import { NonAbatixCategory, USER_ROLE } from '../../Types';
import { ModalContent } from '../UsersPage';
import { NonAbatixCategoryForm } from './NonAbatixCategoryForm';

const ImportStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 8px;
  background-color: #f8f9fa;
`;

const ImportStepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const StepTitle = styled.span`
  color: ${COLORS.darkGray};
  font-size: 1rem;
  font-weight: 600;
`;

const ImportInstructionText = styled.p`
  margin: 0;
  color: ${COLORS.darkGray};
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: center;
`;

const ColumnList = styled.ul`
  margin: 0;
  padding: 12px 16px 12px 32px;
  background-color: ${COLORS.white};
  border-radius: 6px;
  border-left: 3px solid ${COLORS.orange};
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  line-height: 1.8;
`;

const TemplateDownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background-color: ${COLORS.darkTeal};
    transform: translateY(-2px);
  }
`;

const FileDropZone = styled.label<{ $hasFile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 20px;
  border: 2px dashed ${({ $hasFile }) => ($hasFile ? COLORS.teal : COLORS.gray)};
  border-radius: 8px;
  background-color: ${COLORS.white};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.teal};
    background-color: ${COLORS.teal}08;
  }

  input {
    display: none;
  }
`;

const SelectedFileName = styled.span`
  color: ${COLORS.teal};
  font-size: 0.9rem;
  font-weight: 600;
  word-break: break-all;
  text-align: center;
`;

const ImportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ImportContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 560px;
`;

const ImageUploadText = styled.span`
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const ImageUploadSubtext = styled.span`
  color: ${COLORS.lightGray};
  font-size: 0.75rem;
  text-align: center;
`;

const BulkImportComponent: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient('/api/non-abatix/categories/download-template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NonAbatix-Category-Template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Failed to download template', 'error');
    }
  };

  const handleImport = async () => {
    if (!file) {
      showSnackbar('Please select a file', 'error');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClientFormData('/api/non-abatix/categories/import', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Import failed');
      }
      const result = await response.json();
      const msg = result.errors?.length
        ? `Imported ${result.imported} categor${result.imported === 1 ? 'y' : 'ies'}. ${result.errors.length} row(s) had errors.`
        : `Successfully imported ${result.imported} categor${result.imported === 1 ? 'y' : 'ies'}`;
      showSnackbar(msg, result.errors?.length ? 'error' : 'success');
      if (result.imported > 0) {
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      showSnackbar(err.message || 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <ImportWrapper>
      <ImportContent>
        <ImportInstructionText>
          Import multiple product categories at once using an Excel spreadsheet.
          Download the template first to ensure your file has the correct column format.
        </ImportInstructionText>

        <ColumnList>
          <li><strong>Company</strong> — select from dropdown (CID-Name)</li>
          <li><strong>Category Name</strong> — required; unique per company</li>
          <li><strong>Category Code</strong> — optional short code</li>
          <li><strong>Active (Y/N)</strong> — select from dropdown; defaults to Y</li>
          <li>Imported categories are turned on for all of that company's locations. You can uncheck specific locations after import.</li>
        </ColumnList>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>1</StepNumber>
            <StepTitle>Download the template</StepTitle>
          </ImportStepHeader>
          <TemplateDownloadButton type="button" onClick={handleDownloadTemplate}>
            <DownloadIcon size={18} />
            Download Excel Template
          </TemplateDownloadButton>
        </ImportStep>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>2</StepNumber>
            <StepTitle>Upload your completed file</StepTitle>
          </ImportStepHeader>
          <FileDropZone $hasFile={!!file}>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <UploadIcon size={28} color={COLORS.teal} />
            {file ? (
              <SelectedFileName>{file.name}</SelectedFileName>
            ) : (
              <>
                <ImageUploadText>Click to select an .xlsx file</ImageUploadText>
                <ImageUploadSubtext>Only Excel (.xlsx) files are accepted</ImageUploadSubtext>
              </>
            )}
          </FileDropZone>
        </ImportStep>

        <FormActions onCancel={onClose}>
          <FormCancelButton type="button" onClick={onClose}>Cancel</FormCancelButton>
          <FormSubmitButton type="button" onClick={handleImport} disabled={!file || importing}>
            {importing ? 'Importing...' : 'Import Categories'}
          </FormSubmitButton>
        </FormActions>
      </ImportContent>
    </ImportWrapper>
  );
};

const NonAbatixProductCategoryPage: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<NonAbatixCategory | null>(null);

  const handleCloseModal = () => {
    setShowAddModal(false);
    setCategoryToEdit(null);
  };

  const [sortField, setSortField] = useState<string>('category_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const searchFields = ['category_name', 'category_code', 'customer_name', 'location_name', 'description'];
  const { columnSearches, setSearch, clearSearch } = useColumnSearches(searchFields);

  const handleColumnSearch = (field: string, val: string) => {
    setSearch(field, val);
  };

  const handleClearColumnSearch = (field: string) => {
    clearSearch(field);
  };

  const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
    setSortField(field || 'category_name');
    setSortOrder(order ?? 'asc');
  };

  const [CID] = useGetCustomData('/api/company/userregister');
  const [selectedCompany, setSelectedCompany] = useState<string>('');

  const token: any = decode();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (token.roles.id !== USER_ROLE.ADMIN_ABATIX && CID.length > 0 && !selectedCompany) {
      setSelectedCompany(CID[0].OVID);
    }
  }, [CID, selectedCompany]);

  const baseUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedCompany && selectedCompany !== '') {
      params.set('cid', selectedCompany);
    }
    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });
    if (sortField) {
      params.set('sort_field', sortField);
      params.set('sort_order', sortOrder);
    }
    return `/api/non-abatix/categories/manage?${params.toString()}`;
  }, [selectedCompany, columnSearches, sortField, sortOrder]);

  const [categories, loadingCategories, , refetch] = useGetCustomData(baseUrl);

  const categoryList: NonAbatixCategory[] = Array.isArray(categories) ? categories : [];

  const columns: ColumnDef<any>[] = [
    {
      field: 'customer_name',
      headerName: 'Account',
      width: 200,
      sortable: true,
      defaultFirst: true,
      renderHeader: () => (
        <SearchableHeader
          field="customer_name"
          headerName="Account"
          searchValue={columnSearches.customer_name}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'location_name',
      headerName: 'Location',
      width: 180,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="location_name"
          headerName="Location"
          searchValue={columnSearches.location_name}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
      renderCell: (params: any) => (
        <span title={params.row.location_names || params.value || ''}>{params.value ?? '—'}</span>
      ),
    },
    {
      field: 'category_name',
      headerName: 'Category Name',
      width: 220,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="category_name"
          headerName="Category Name"
          searchValue={columnSearches.category_name}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'category_code',
      headerName: 'Category Code',
      width: 160,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="category_code"
          headerName="Category Code"
          searchValue={columnSearches.category_code}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'active_flag',
      headerName: 'Active',
      width: 100,
      sortable: true,
      renderCell: (params) => (params.value ? 'Y' : 'N'),
    },
    { field: 'full_name', headerName: 'Created By', width: 150, sortable: true },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
          <EditButton onClick={() => { setCategoryToEdit(params.row); setShowAddModal(true); }}>
            <PencilIcon />
          </EditButton>
        </div>
      ),
    },
  ];

  const handleSubmit = async (formData: any) => {
    const isNew = !categoryToEdit;

    const payload: any = {
      category_name: formData.category_name,
      category_code: formData.category_code || '',
      active_flag: formData.active_flag ?? 1,
      customer_id: formData.customer_id || null,
      location_ids: Array.isArray(formData.location_ids) ? formData.location_ids : [],
    };

    const url = isNew
      ? '/api/non-abatix/categories/manage'
      : `/api/non-abatix/categories/manage/${categoryToEdit!.category_id}`;
    const method = isNew ? 'POST' : 'PUT';

    const form = new FormData();
    form.append('data', JSON.stringify(payload));
    if (formData.image) {
      form.append('image', formData.image);
    }

    try {
      const response = await apiClientFormData(url, { method, body: form });

      if (!response.ok) {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to save category', 'error', 'Something went wrong!');
        return;
      }

      const saved = await response.json();
      showSnackbar(
        `${saved.category_name} was successfully ${isNew ? 'added' : 'updated'}.`,
        'success',
        isNew ? 'Successfully Added!' : 'Successfully Updated!'
      );
      refetch();
      setShowAddModal(false);
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Something went wrong!');
    }
  };

  return (
    <PageContainer>
      <Header title="Non-ABATIX Product Category" count={categoryList.length} hideCount={false}>
        <AddButton onClick={() => setShowAddModal(true)}>+</AddButton>
        <BulkImportLink onClick={() => setShowImportModal(true)}>Bulk Import</BulkImportLink>
      </Header>

      {showAddModal && (
        <ModalOverlay onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
            <ModalHeader>{categoryToEdit ? 'Edit' : 'Add'} Non-ABATIX Category</ModalHeader>
            <NonAbatixCategoryForm
              onSubmit={handleSubmit}
              onCancel={handleCloseModal}
              defaultValues={categoryToEdit}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {showImportModal && (
        <ModalOverlay onClick={() => setShowImportModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowImportModal(false)}>×</CloseButton>
            <ModalHeader>Bulk Import</ModalHeader>
            <BulkImportComponent
              onClose={() => setShowImportModal(false)}
              onSuccess={() => refetch()}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      <DataTable
        columns={columns}
        data={categoryList}
        loading={loadingCategories}
        pageKey="non-abatix-category"
        idField="category_id"
        hideColumnFilters={true}
        hideSearchBar={true}
        serverSideSort={true}
        defaultSort={[{ field: 'category_name', sort: 'asc' }]}
        onSortModelChange={handleSortChange}
        defaultColumnVisibility={{ category_code: false }}
      >
        <CompanySelect
          companies={CID}
          selectedCompany={selectedCompany}
          onChange={setSelectedCompany}
        />
      </DataTable>
    </PageContainer>
  );
};

export default NonAbatixProductCategoryPage;
